<template>
    <CollapsibleWidget :is-opened="isOpen" collapsed-height="47" @onChange="toggleOpen">
        <template #name>{{ editable.label }}</template>
        <template #controls>
            <EditableRestrictionControl v-if="showRestrictionControl" :editable="editable" />
        </template>

        <div class="deliverable-values editable-widget__body">
            <div v-if="readOnly" class="editable-widget__body--readonly" title="Read Only" @click.stop.prevent></div>
            <ExpandableTextInput
                class="deliverable-value-item"
                :label="EditableGroupScopeLabel"
                :can-change-visibility="false"
                :is-expanded="isExpanded(null)"
                :model-value="getModelValue(editable, groupsAffectingEditable, null)"
                :settings="getEditableSettings(editable, groupsAffectingEditable, null)"
                :scope="OverwriteScope.EditableGroup"
                :overwrite="getOverwrite(editable, groupsAffectingEditable, null)"
                placeholder="Enter value"
                :lockable="false"
                @collapse="onCollapse(null)"
                @input="throttledValueChange"
                @expand="onExpand(null)"
                @restore="onValueRestore(null)"
                @updateSettings="onUpdateSettings({ ...$event, templateId: null })"
            >
                <template #editor="editorProps">
                    <CssEditor :model-value="editorProps.modelValue" @update:modelValue="editorProps.onChange" />
                </template>
            </ExpandableTextInput>

            <ExpandableTextInput
                v-for="(label, templateId) in templateLabels"
                v-show="isOpen"
                :key="templateId"
                class="deliverable-value-item"
                :can-change-visibility="false"
                :is-expanded="isExpanded(templateId)"
                :label="label"
                :label-color="getLabelColor(editable, groupsAffectingEditable, templateId)"
                :model-value="getModelValue(editable, groupsAffectingEditable, templateId)"
                :settings="getEditableSettings(editable, groupsAffectingEditable, templateId)"
                :scope="OverwriteScope.EditableGroupTemplate"
                :overwrite="getOverwrite(editable, groupsAffectingEditable, templateId)"
                placeholder="Enter value"
                @collapse="onCollapse(templateId)"
                @input="throttledValueChange($event, templateId)"
                @expand="onExpand(templateId)"
                @removeOverwrite="onRemoveOverwrite"
                @restore="onValueRestore(templateId)"
                @updateSettings="onUpdateSettings({ ...$event, templateId })"
            >
                <template #editor="editorProps">
                    <CssEditor :model-value="editorProps.modelValue" @update:modelValue="editorProps.onChange" />
                </template>
            </ExpandableTextInput>
        </div>
    </CollapsibleWidget>
</template>

<script lang="ts">
import { defineComponent, toRef, type PropType } from "vue";
import throttle from "lodash.throttle";
import { OverwriteScope } from "shared-utils/enums/overwrites";
import type { CSSSettings, Editable } from "shared-types";

import CollapsibleWidget from "@/components/Campaign/widgets/CollapsibleWidget.vue";
import ExpandableTextInput from "@/components/Campaign/ExpandableTextInput.vue";
import EditableRestrictionControl from "@/components/Campaign/Editables/EditableRestrictionControl.vue";
import CssEditor from "@/components/Campaign/widgets/CssEditor.vue";

import { useToggleEditableItem } from "@/composables/useToggleEditableItem";
import { useOverwrite } from "@/composables/useOverwrite";
import { useEditable } from "@/composables/useEditable";
import { useEditableMethods } from "@/composables/useEditableMethods";
import { useEditor } from "@/composables/useEditor";

import { EditableGroupScopeLabel } from "@/enums/overwrites";

export default defineComponent({
    name: "EditableCssWidget",
    components: {
        CssEditor,
        EditableRestrictionControl,
        ExpandableTextInput,
        CollapsibleWidget
    },

    props: {
        editable: {
            type: Object as PropType<Editable>,
            required: true
        },

        groupsAffectingEditable: {
            type: Array as PropType<string[]>,
            required: true
        },

        allExpanded: {
            type: Boolean,
            default: true
        },

        readOnly: {
            type: Boolean,
            default: false
        },

        showRestrictionControl: {
            type: Boolean,
            default: true
        }
    },

    setup(props, { emit }) {
        const allExpanded = toRef(props, "allExpanded");
        const editable = toRef(props, "editable");

        const { isOpen, toggleOpen } = useToggleEditableItem({ allExpanded });
        const { getOverwrite, removeOverwrite } = useOverwrite();
        const { getEditableValue, getLabelColor, update, restore, getEditableSettings, updateSettings } =
            useEditableMethods({ emit });
        const { templateLabels } = useEditable({ editable });
        const { selectedLanguage } = useEditor();

        return {
            isOpen,
            toggleOpen,
            getOverwrite,
            getEditableSettings,
            getEditableValue,
            getLabelColor,
            templateLabels,
            updateSettings,
            update,
            restore,
            removeOverwrite,
            selectedLanguage
        };
    },

    data() {
        return {
            expandedTemplateId: undefined as string | null | undefined,
            EditableGroupScopeLabel,
            OverwriteScope,
            throttledValueChange: Function as (value: unknown, templateId?: string) => void
        };
    },

    created() {
        this.throttledValueChange = throttle((value, templateId) => {
            this.onValueChange(value, templateId);
        }, 100);
    },

    methods: {
        getModelValue(editable: Editable, groupsAffectingEditable: string[], templateId: string | null) {
            return this.getEditableValue(editable, groupsAffectingEditable, templateId) as string | null;
        },

        isExpanded(templateId: string | null) {
            return this.expandedTemplateId === templateId;
        },

        onCollapse(templateId: string | null) {
            if (this.isExpanded(templateId)) {
                this.expandedTemplateId = undefined;
            }
        },

        onExpand(templateId: string | null) {
            this.expandedTemplateId = templateId;
        },

        onValueChange(value: string, templateId: string | null) {
            this.update({
                editable: this.editable,
                updateData: {
                    value
                },
                groupsAffectingEditable: this.groupsAffectingEditable,
                templateId
            });
        },

        onRemoveOverwrite(overwriteId: string) {
            this.removeOverwrite(overwriteId);
        },

        onValueRestore(templateId: string | null) {
            this.restore({
                editable: this.editable,
                templateId,
                language: this.selectedLanguage
            });
        },

        onUpdateSettings({
            settings,
            newSettings,
            templateId
        }: {
            settings: CSSSettings;
            newSettings: CSSSettings;
            templateId: string | null;
        }) {
            const mergedSettings = {
                ...settings,
                ...newSettings
            };
            this.updateSettings({
                editable: this.editable,
                settings: mergedSettings,
                templateId,
                groupsAffectingEditable: this.groupsAffectingEditable
            });
        }
    }
});
</script>
